import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-cream to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-5xl lg:text-6xl font-bold text-charcoal mb-6">
              Meet LaTonya Duncan
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Two-time stroke survivor, cervical cancer survivor, author, wife, mother, grandmother, Founder and CEO, Life Coach, Relationship Specialist, and passionate advocate for health and wellness
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/93cce08d5e18149bb13f66d785fc059f.jpeg"
                    alt="LaTonya Duncan - Founder"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-coral text-white p-6 rounded-2xl shadow-xl">
                  <div className="text-4xl font-bold">10+</div>
                  <div className="text-sm">Years of Advocacy</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-serif text-4xl font-bold text-charcoal">My Journey</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Hi, I'm <strong>LaTonya Duncan</strong>, a two-time stroke survivor, wife, mother, grandmother, and passionate advocate for health and wellness. My journey with heart health has been deeply personal. After experiencing two strokes, I realized firsthand how critical education, awareness, and preventative care are for our communities—especially for women who are often overlooked when it comes to heart disease and related health risks.
                </p>
                <p>
                  My dedication to women's heart health began with volunteering for the <strong>Go Red for Women campaign</strong> with the American Heart Association. Through this work, I witnessed the power of education and awareness in saving lives. I also saw gaps—communities and individuals who didn't have access to the resources, knowledge, or support they needed to protect their health. That gap inspired me to do more.
                </p>
                <p>
                  In <strong>2021</strong>, I followed my vision and founded <strong>1 Heart Health and Wellness</strong>, a nonprofit organization committed to educating, empowering, and equipping communities with the tools they need to live healthier lives. Our mission is simple but vital: to focus on mind, body, and spirit wellness by providing education and support on topics including heart health, mental health, breast cancer awareness, domestic violence prevention, and HIV awareness.
                </p>
                <p>
                  At 1 Heart, we believe <strong>knowledge saves lives</strong>. We believe that prevention is powerful. And we believe that everyone deserves access to health resources—no matter their background. Through workshops, events, community programs, and fashion-driven awareness initiatives, we strive to inspire individuals to take control of their health and make choices that lead to longer, healthier, and more fulfilling lives.
                </p>
                <p>
                  My journey as a survivor fuels everything we do. It's my story, my vision, and my commitment that guide 1 Heart Health and Wellness every day. I'm proof that even after life's hardest challenges, you can rise, lead, and create change—not just for yourself, but for your community.
                </p>
                <p className="font-semibold text-coral text-lg">
                  Together, we can create a world where health awareness isn't optional—it's essential, accessible, and empowering.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Board Members Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Our Board Members
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Dedicated professionals committed to advancing health education and community wellness
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Dr. Yulanda King */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-cream rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
            >
              <div className="relative h-80 w-full">
                <img
                  src="https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/882c78fbd2dc975d4e17bafb250eea18.jpeg"
                  alt="Dr. Yulanda King"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-2xl font-bold text-charcoal">
                    Dr. Yulanda King
                  </h3>
                  <div className="w-12 h-12 flex items-center justify-center bg-coral/10 rounded-full">
                    <i className="ri-stethoscope-line text-xl text-coral"></i>
                  </div>
                </div>
                <p className="text-coral font-semibold text-sm mb-3">
                  DNP, CRNP, PMHNP
                </p>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  Board Member since 2023. Dr. King has volunteered her services to assist in education of Diabetes, Mental Health, and keeps us updated on vaccinations and other illnesses.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-coral/10 text-coral px-3 py-1 rounded-full text-xs font-medium">
                    Diabetes Education
                  </span>
                  <span className="bg-purple/10 text-purple px-3 py-1 rounded-full text-xs font-medium">
                    Mental Health
                  </span>
                  <span className="bg-teal/10 text-teal px-3 py-1 rounded-full text-xs font-medium">
                    Preventative Care
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Dr. Akeba Thompson */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-cream rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
            >
              <div className="relative h-80 w-full">
                <img
                  src="https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/0fa628df5bd7f421f7dd398f129921e3.jpeg"
                  alt="Dr. Akeba Thompson"
                  className="w-full h-full object-cover object-[center_20%]"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-2xl font-bold text-charcoal">
                    Dr. Akeba Thompson
                  </h3>
                  <div className="w-12 h-12 flex items-center justify-center bg-coral/10 rounded-full">
                    <i className="ri-heart-pulse-line text-xl text-coral"></i>
                  </div>
                </div>
                <p className="text-coral font-semibold text-sm mb-3">
                  CRNP, Cardiac Nurse Practitioner
                </p>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  Board Member since 2023. Akeba has volunteered her services on Heart Health education, recognizing warning signs, and promoting healthy eating habits.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-coral/10 text-coral px-3 py-1 rounded-full text-xs font-medium">
                    Heart Health
                  </span>
                  <span className="bg-purple/10 text-purple px-3 py-1 rounded-full text-xs font-medium">
                    Warning Signs
                  </span>
                  <span className="bg-teal/10 text-teal px-3 py-1 rounded-full text-xs font-medium">
                    Nutrition
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Jasmine Cullins */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-cream rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
            >
              <div className="relative h-80 w-full">
                <img
                  src="https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/d741e1ebd19c564cb681e013db24d143.jpeg"
                  alt="Jasmine Cullins"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-2xl font-bold text-charcoal">
                    Jasmine Cullins
                  </h3>
                  <div className="w-12 h-12 flex items-center justify-center bg-coral/10 rounded-full">
                    <i className="ri-shirt-line text-xl text-coral"></i>
                  </div>
                </div>
                <p className="text-coral font-semibold text-sm mb-3">
                  Fashion & Health Education Specialist
                </p>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  Faithful volunteer since 2022. Jasmine assists with the fashion portion of educating through fashion on certain health issues, bringing creativity and awareness together.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-coral/10 text-coral px-3 py-1 rounded-full text-xs font-medium">
                    Fashion Education
                  </span>
                  <span className="bg-purple/10 text-purple px-3 py-1 rounded-full text-xs font-medium">
                    Creative Awareness
                  </span>
                  <span className="bg-teal/10 text-teal px-3 py-1 rounded-full text-xs font-medium">
                    Community Outreach
                  </span>
                </div>
              </div>
            </motion.div>

            {/* L.M. Rawls */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-cream rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
            >
              <div className="relative h-80 w-full">
                <img
                  src="https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/bb6b5697e9a3e7f5567dcc0bdcac0fe4.jpeg"
                  alt="L.M. Rawls"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-2xl font-bold text-charcoal">
                    L.M. Rawls
                  </h3>
                  <div className="w-12 h-12 flex items-center justify-center bg-coral/10 rounded-full">
                    <i className="ri-hand-heart-line text-xl text-coral"></i>
                  </div>
                </div>
                <p className="text-coral font-semibold text-sm mb-3">
                  LMT, Cfm
                </p>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  Dedicated volunteer since 2022. L.M. Rawls provides community service massage therapy and certified mastectomy fitting services, supporting healing and recovery.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-coral/10 text-coral px-3 py-1 rounded-full text-xs font-medium">
                    Massage Therapy
                  </span>
                  <span className="bg-purple/10 text-purple px-3 py-1 rounded-full text-xs font-medium">
                    Mastectomy Care
                  </span>
                  <span className="bg-teal/10 text-teal px-3 py-1 rounded-full text-xs font-medium">
                    Community Service
                  </span>
                </div>
              </div>
            </motion.div>

            {/* T'Sharin Moncrief */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-cream rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
            >
              <div className="relative h-80 w-full">
                <img
                  src="https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/0374e9fdf324d12649a8f708ce0bfd13.jpeg"
                  alt="T'Sharin Moncrief"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-2xl font-bold text-charcoal">
                    T'Sharin Moncrief
                  </h3>
                  <div className="w-12 h-12 flex items-center justify-center bg-coral/10 rounded-full">
                    <i className="ri-shield-user-line text-xl text-coral"></i>
                  </div>
                </div>
                <p className="text-coral font-semibold text-sm mb-3">
                  Domestic Violence Advisor
                </p>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  Volunteer since 2023. T'Sharin provides critical guidance and support for domestic violence prevention, creating safe spaces and empowering survivors with resources and advocacy.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-coral/10 text-coral px-3 py-1 rounded-full text-xs font-medium">
                    DV Prevention
                  </span>
                  <span className="bg-purple/10 text-purple px-3 py-1 rounded-full text-xs font-medium">
                    Survivor Support
                  </span>
                  <span className="bg-teal/10 text-teal px-3 py-1 rounded-full text-xs font-medium">
                    Advocacy
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Autussi O' Duncan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-cream rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
            >
              <div className="relative h-80 w-full">
                <img
                  src="https://static.readdy.ai/image/56b439000ec6e974a9f2c259ba0dbcf0/035edbd6d84e53afdb869f317f67bf2a.jpeg"
                  alt="Autussi O' Duncan"
                  className="w-full h-full object-cover object-[center_10%]"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-serif text-2xl font-bold text-charcoal">
                    Autussi O' Duncan
                  </h3>
                  <div className="w-12 h-12 flex items-center justify-center bg-coral/10 rounded-full">
                    <i className="ri-user-star-line text-xl text-coral"></i>
                  </div>
                </div>
                <p className="text-coral font-semibold text-sm mb-3">
                  Model Coordinator & Director's Assistant
                </p>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  Volunteer since 2021. Autussi serves as Model Coordinator, Director's Assistant, and Kids Models Coach, bringing organization and mentorship to our fashion education initiatives.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-coral/10 text-coral px-3 py-1 rounded-full text-xs font-medium">
                    Model Coordination
                  </span>
                  <span className="bg-purple/10 text-purple px-3 py-1 rounded-full text-xs font-medium">
                    Youth Mentorship
                  </span>
                  <span className="bg-teal/10 text-teal px-3 py-1 rounded-full text-xs font-medium">
                    Event Management
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-br from-coral/5 via-purple/5 to-pink/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Life Coaching & Relationship Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Empowering individuals and couples to create meaningful connections, overcome challenges, and build fulfilling lives through personalized coaching and guidance
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Life Coaching */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all"
            >
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-coral to-pink rounded-2xl mb-6">
                <i className="ri-compass-3-line text-4xl text-white"></i>
              </div>
              <h3 className="font-serif text-3xl font-bold text-charcoal mb-4">Life Coaching</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Navigate life's challenges with clarity and confidence. As a certified life coach and survivor, I bring personal experience and professional expertise to help you overcome obstacles and achieve your goals.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-coral text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Personal Growth & Development</h4>
                    <p className="text-gray-600 text-sm">Discover your strengths and unlock your full potential</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-coral text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Goal Setting & Achievement</h4>
                    <p className="text-gray-600 text-sm">Create actionable plans to reach your dreams</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-coral text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Overcoming Life Transitions</h4>
                    <p className="text-gray-600 text-sm">Navigate change with resilience and grace</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-coral text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Health & Wellness Coaching</h4>
                    <p className="text-gray-600 text-sm">Build sustainable habits for mind, body, and spirit</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Relationship Specialist */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all"
            >
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-purple to-pink rounded-2xl mb-6">
                <i className="ri-hearts-line text-4xl text-white"></i>
              </div>
              <h3 className="font-serif text-3xl font-bold text-charcoal mb-4">Relationship Specialist</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Strengthen your connections and build healthier relationships. With years of experience as a wife, mother, and relationship specialist, I help individuals and couples create lasting bonds.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-purple text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Couples Coaching</h4>
                    <p className="text-gray-600 text-sm">Improve communication and deepen your connection</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-purple text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Communication Skills</h4>
                    <p className="text-gray-600 text-sm">Learn to express needs and listen with empathy</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-purple text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Conflict Resolution</h4>
                    <p className="text-gray-600 text-sm">Transform disagreements into opportunities for growth</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-purple text-xl mt-1"></i>
                  <div>
                    <h4 className="font-semibold text-charcoal mb-1">Building Trust & Intimacy</h4>
                    <p className="text-gray-600 text-sm">Create deeper emotional and spiritual connections</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Coaching Approach */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-charcoal to-gray-800 rounded-3xl p-10 text-white"
          >
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-2xl mx-auto mb-6">
                <i className="ri-lightbulb-flash-line text-3xl text-white"></i>
              </div>
              <h3 className="font-serif text-3xl font-bold mb-4">My Coaching Approach</h3>
              <p className="text-white/90 text-lg leading-relaxed mb-8">
                Drawing from my personal journey as a two-time stroke survivor and cervical cancer survivor, I bring authenticity, empathy, and real-world wisdom to every coaching session. I believe in meeting you where you are, honoring your unique story, and empowering you with practical tools for lasting transformation.
              </p>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div className="bg-white/10 rounded-2xl p-6">
                  <i className="ri-user-heart-line text-3xl text-coral mb-3"></i>
                  <h4 className="font-bold text-lg mb-2">Personalized</h4>
                  <p className="text-white/80 text-sm">Every session is tailored to your specific needs and goals</p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6">
                  <i className="ri-shield-check-line text-3xl text-coral mb-3"></i>
                  <h4 className="font-bold text-lg mb-2">Safe Space</h4>
                  <p className="text-white/80 text-sm">Confidential, judgment-free environment for growth</p>
                </div>
                <div className="bg-white/10 rounded-2xl p-6">
                  <i className="ri-trophy-line text-3xl text-coral mb-3"></i>
                  <h4 className="font-bold text-lg mb-2">Results-Focused</h4>
                  <p className="text-white/80 text-sm">Actionable strategies that create real, lasting change</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mt-12"
          >
            <p className="text-gray-700 text-lg mb-6">
              Ready to start your transformation journey?
            </p>
            <a
              href="/services"
              className="inline-flex items-center bg-gradient-to-r from-coral to-pink text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
            >
              View Packages & Pricing
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-8 shadow-lg"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-coral/10 rounded-2xl mb-6">
                <i className="ri-heart-pulse-line text-3xl text-coral"></i>
              </div>
              <h3 className="font-serif text-3xl font-bold text-charcoal mb-4">Our Mission</h3>
              <p className="text-gray-700 leading-relaxed">
                To educate, empower, and equip communities with the tools they need to live healthier lives. We focus on mind, body, and spirit wellness through comprehensive programs on heart health, mental wellness, breast cancer awareness, domestic violence prevention, and HIV awareness. We believe that knowledge saves lives, and every person deserves access to life-changing health information.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl p-8 shadow-lg"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-purple/10 rounded-2xl mb-6">
                <i className="ri-eye-line text-3xl text-purple"></i>
              </div>
              <h3 className="font-serif text-3xl font-bold text-charcoal mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed">
                A world where health awareness isn't optional—it's essential, accessible, and empowering. Where every community has access to quality health education and support services. Where no one suffers from a preventable condition due to lack of knowledge. We envision thriving communities where individuals are empowered to make informed health decisions for themselves and their families.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do and every decision we make
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: 'ri-heart-line',
                title: 'Compassion',
                description: 'We approach every individual with empathy, understanding, and genuine care.',
              },
              {
                icon: 'ri-lightbulb-line',
                title: 'Education',
                description: 'We believe knowledge is power and strive to make health information accessible to all.',
              },
              {
                icon: 'ri-team-line',
                title: 'Community',
                description: 'We build strong, supportive networks that uplift and empower everyone.',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Prevention',
                description: 'We focus on preventative care and early intervention to save lives.',
              },
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-coral/10 rounded-xl mx-auto mb-4">
                  <i className={`${value.icon} text-3xl text-coral`}></i>
                </div>
                <h3 className="font-bold text-xl text-charcoal mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Timeline */}
      <section className="py-20 bg-gradient-to-b from-white to-cream">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-charcoal mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-gray-600">
              Milestones that shaped our organization
            </p>
          </motion.div>

          <div className="space-y-8">
            {[
              {
                year: 'Early 2014',
                title: 'Personal Journey',
                description: 'LaTonya experienced two strokes, igniting her passion for heart health awareness and prevention.',
              },
              {
                year: '2010s',
                title: 'Advocacy Begins',
                description: 'Started volunteering with Go Red for Women campaign through the American Heart Association.',
              },
              {
                year: '2019',
                title: 'Foundation',
                description: 'Founded 1 Heart Health and Wellness to fill gaps in community health education and resources.',
              },
              {
                year: '2020',
                title: 'Program Expansion',
                description: 'Launched comprehensive programs covering heart health, mental wellness, breast cancer, domestic violence, and HIV awareness.',
              },
              {
                year: '2022',
                title: 'Board Formation',
                description: 'Dr. Yulanda King joined as board member, bringing expertise in diabetes, mental health, and preventative care education.',
              },
              {
                year: '2025',
                title: 'Today',
                description: 'Continuing to empower communities with education, support, and resources for healthier lives.',
              },
            ].map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6"
              >
                <div className="flex-shrink-0">
                  <div className="w-24 h-20 flex items-center justify-center bg-coral text-white rounded-full font-bold text-sm text-center px-2">
                    {milestone.year}
                  </div>
                </div>
                <div className="flex-1 bg-white rounded-2xl p-6 shadow-md">
                  <h3 className="font-bold text-xl text-charcoal mb-2">{milestone.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}